import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Select, Button, message, DatePicker, Dropdown } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import AddImageModal from './components/addImageModal';
import ConfirmModal from '../../../components/confirm-modal';
import PaginationComponent from '../../../components/pagination';
import { useSelector } from 'react-redux';
import moreIcon from 'assets/images/icon_btn_More.svg';
import deleteBatch from 'assets/images/icon_btn_delete_text.svg';
import approveIcon from 'assets/images/approve_action.svg';
import deleteAction from 'assets/images/icon_btn_delete_table.svg';
import addIcon from 'assets/images/addIcon.png';
import { getResponseData } from 'api/client';
import { usePermissions } from 'hooks';
import * as Permissions from 'constants/permissions';
import { formatDateString, displayText } from 'utils';
import { approvalStatusOptions, imageIssueType } from '../../../constants/knowledge-base';
import ImageView from 'components/image-view/ImageView';
import * as api from 'api';
import '../index.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ImageBase = () => {
	const [dataSource, setDataSource] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalMode, setModalMode] = useState('add'); // add, edit, approve
	const [hasUploaded, setHasUploaded] = useState(false);
	const [imageType, setImageType] = useState(undefined);
	const [approvalStatus, setApprovalStatus] = useState(undefined);
	const [scene, setScene] = useState(undefined);
	const [dateRange, setDateRange] = useState();

	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const isBatchDelete = useRef(false);

	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');

	const [editPreviewImage, setEditPreviewImage] = useState(null);
	const [editImageName, setEditImageName] = useState('');
	const [editRecordId, setEditRecordId] = useState(null);
	const [editOssKey, setEditOssKey] = useState(null);
	const [editFormValues, setEditFormValues] = useState({});
	const [editImageFile, setEditImageFile] = useState(null); // 新增：保存下载的原图片文件

	const [applicationOptions, setApplicationOptions] = useState([]);
	const selectedAppId = useRef(null);

	useEffect(() => {
		fetchApplications();
	}, []);

	useEffect(() => {
		if (hasUploaded && !isModalVisible) {
			handleResetFilter();
			setHasUploaded(false);
		}
	}, [hasUploaded, isModalVisible]);

	const handleSearch = () => {
		setPagination((prev) => ({ ...prev, current: 1 }));
		fetchImageList(1, pagination.pageSize, false, selectedAppId.current);
	};

	const { permissions } = useSelector((state) => state.user);

	const fetchImageList = async (pageIndex, pageSize, resetFilter, appId) => {
		try {
			console.log(imageType, approvalStatus, scene, dateRange);
			const params = {
				page: pageIndex || pagination.current,
				size: pageSize || pagination.pageSize,
				issueType: imageType,
				status: approvalStatus,
				scene: scene,
				startTime: dateRange?.[0]?.format('YYYY-MM-DD HH:mm'),
				endTime: dateRange?.[1]?.format('YYYY-MM-DD HH:mm'),
			};
			const resetFilterParams = {
				page: pageIndex || pagination.current,
				size: pageSize || pagination.pageSize,
			};
			const newParams = resetFilter ? resetFilterParams : params;
			const response = getResponseData(await api.getImageList(appId, newParams));
			console.log(response);
			if (!response || !response.files) {
				console.log('response.files', response.files);
			} else if (response && response.files) {
				setDataSource(response.files);
				setPagination((prev) => ({ ...prev, total: response.total || 0 }));
			}
		} catch (error) {
			console.error('获取图片列表失败:', error);
		}
	};

	const handleUpload = () => {
		setIsModalVisible(true);
		setModalMode('add');
		setHasUploaded(false);
	};

	const handleModalCancel = () => {
		setIsModalVisible(false);
		setModalMode('add');

		if (editPreviewImage) {
			URL.revokeObjectURL(editPreviewImage);
			setEditPreviewImage(null);
		}
		setEditImageName('');
		setEditImageFile(null); // 清理文件对象
	};

	const onSelectChange = (newSelectedRowKeys) => {
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const handleBatchDelete = () => {
		if (selectedRowKeys.length === 0) {
			message.warning('Please select items to delete');
			return;
		}
		isBatchDelete.current = true;
		setDeleteModalVisible(true);
	};

	const handleDelete = (id, ossKey, filename) => {
		isBatchDelete.current = false;
		const newTarget = { id, ossKey, filename };
		setDeleteTarget(newTarget);
		setDeleteModalVisible(true);
	};

	function onConfirmDelete() {
		if (isBatchDelete.current) {
			handleConfirmBatchDelete();
		} else {
			handleConfirmDelete();
		}
	}

	const handleConfirmBatchDelete = async () => {
		try {
			const selectedOssKeys = dataSource.filter((item) => selectedRowKeys.includes(item.id)).map((item) => item.ossKey);
			await api.deleteImage(
				{
					ossKeys: selectedOssKeys,
				},
				selectedAppId.current,
			);
			setSelectedRowKeys([]);
			setDeleteModalVisible(false);
			fetchImageList(pagination.current, pagination.pageSize, false, selectedAppId.current);
			message.success('Selected items deleted successfully');
		} catch (error) {
			console.error('批量删除失败：', error);
		} finally {
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		try {
			await api.deleteImage(
				{
					ossKeys: [deleteTarget.ossKey],
				},
				selectedAppId.current,
			);

			setDeleteModalVisible(false);
			fetchImageList(pagination.current, pagination.pageSize, false, selectedAppId.current);
			message.success('Item deleted successfully');
		} catch (error) {
			console.error('删除失败：', error);
		} finally {
		}
	};

	const handleEditOrApprove = async (record, isApprove) => {
		setIsModalVisible(true);
		isApprove ? setModalMode('approve') : setModalMode('edit');
		setEditRecordId(record.id); // Store the record ID
		setEditOssKey(record.ossKey); // Store the ossKey
		setEditFormValues({
			issueTypes: record.issueType || [],
			scene: record.scene,
			position: record.position,
			issueReason: record.description || '',
		});

		if (record && record.filename) {
			try {
				const response = await api.downloadImage({ ossKey: record.ossKey }, selectedAppId.current);
				const url = URL.createObjectURL(response);
				const file = new File([response], record.filename, { type: response.type });
				setEditPreviewImage(url);
				setEditImageName(record.filename);
				setEditImageFile(file);
			} catch (error) {
				console.error('下载编辑图片失败:', error);
				setEditPreviewImage(null);
				setEditImageName('');
				setEditImageFile(null);
			}
		}
	};
	const handleTableChange = (pagination) => {
		setPagination(pagination);
		fetchImageList(pagination.current, pagination.pageSize, false, selectedAppId.current);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		columnWidth: 40,
	};
	const handleDownload = async (record) => {
		try {
			const response = await api.downloadImage({ ossKey: record.ossKey }, selectedAppId.current);
			const blobData = response;

			const fileExtension = record.filename?.split('.').pop()?.toLowerCase();
			let mimeType = 'application/octet-stream';
			switch (fileExtension) {
				case 'jpg':
				case 'jpeg':
					mimeType = 'image/jpeg';
					break;
				case 'png':
					mimeType = 'image/png';
					break;
				case 'gif':
					mimeType = 'image/gif';
					break;
				case 'bmp':
					mimeType = 'image/bmp';
					break;
				case 'webp':
					mimeType = 'image/webp';
					break;
				default:
					mimeType = record?.type || 'application/octet-stream';
			}

			const blob = new Blob([blobData], { type: mimeType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', record.filename);
			document.body.appendChild(link);
			link.click();
			setTimeout(() => {
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			}, 100);
			message.success('Image downloaded successfully');
		} catch (error) {
			console.error('下载图片失败：', error);
		}
	};

	const handleResetFilter = async () => {
		setImageType(undefined);
		setApprovalStatus(undefined);
		setScene(undefined);
		setDateRange(undefined);
		setPagination((prev) => ({ ...prev, current: 1 }));
		await fetchImageList(1, pagination.pageSize, true, selectedAppId.current);
	};

	const handleImagePreview = (imageUrl, filename) => {
		setPreviewImage(imageUrl);
		setPreviewTitle(filename);
		setPreviewVisible(true);
	};

	const handlePreviewClose = () => {
		setPreviewVisible(false);
		setPreviewImage('');
		setPreviewTitle('');
	};

	const issueScenarioOptions = [
		{ value: 0, label: '生产线上' },
		{ value: 1, label: '仓库' },
		{ value: 2, label: '货架 或 物流中' },
		{ value: 3, label: '其他' },
	];

	const getIssueTypeLabel = (value) => {
		const option = imageIssueType.find((opt) => opt.value === value);
		return option ? option.label : value;
	};

	const getIssueScenarioLabel = (value) => {
		const option = issueScenarioOptions.find((opt) => opt.value === value);
		return option ? option.label : value;
	};

	const handleKeyDown = useCallback(
		(event) => {
			if (!previewVisible) return;
			switch (event.key) {
				case 'Escape':
					handlePreviewClose();
					break;
				default:
					break;
			}
		},
		[previewVisible],
	);

	async function fetchApplications() {
		try {
			api.getApplications().then((apps) => {
				const options = apps.map((item) => ({
					key: item.id,
					label: item.applicationName,
				}));
				setApplicationOptions(options);
				if (!selectedAppId.current && options.length > 0) {
					const appId = apps[0].id;
					selectedAppId.current = appId;
					fetchImageList(1, pagination.pageSize, true, appId);
				}
			});
		} catch (error) {
			console.error('e', error);
		}
	}

	const selectedAppChanged = (appId) => {
		selectedAppId.current = appId;
		fetchImageList(1, pagination.pageSize, true, appId);
	};

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	const columns = [
		{
			title: 'Image',
			dataIndex: 'Image',
			maxWidth: 380,
			key: 'Image',
			render: (text, record) => (
				<ImageView
					filename={record.filename}
					ossKey={record.ossKey}
					appId={selectedAppId.current}
					onPreview={handleImagePreview}
				/>
			),
		},
		{
			title: 'ID',
			dataIndex: 'filename',
			maxWidth: 200,
			key: 'filename',
			render: (text) => <div className="title-cell">{text}</div>,
		},
		{
			title: 'Issue Type',
			dataIndex: 'issueType',
			key: 'issueType',
			width: 200,
			render: (text) => {
				if (!text) return '';
				if (Array.isArray(text)) {
					return text.map((value) => getIssueTypeLabel(value)).join(', ');
				}
				return getIssueTypeLabel(text);
			},
		},
		{
			title: 'Issue Scenario',
			dataIndex: 'scene',
			key: 'scene',
			width: 150,
			render: (text) => {
				if (Array.isArray(text)) {
					return text.map((value) => getIssueScenarioLabel(value)).join(', ');
				}
				return getIssueScenarioLabel(text);
			},
		},
		{
			title: 'Issue Position',
			dataIndex: 'position',
			key: 'position',
			width: 120,
			render: (text) => {
				if (!text) return '';
				if (Array.isArray(text)) {
					return text.join(', ');
				}
				return text;
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 130,
			render: (text) => {
				if (text === undefined || text === null) return '';

				// Map numeric status to labels
				let statusLabel;
				let isApproved;

				if (text === 1 || text === '1') {
					statusLabel = 'Approved';
					isApproved = true;
				} else if (text === 0 || text === '0') {
					statusLabel = 'Pending';
					isApproved = false;
				} else {
					// Fallback for any existing string values
					statusLabel = text;
					isApproved = text.toLowerCase() === 'approved';
				}

				return (
					<span
						style={{
							backgroundColor: isApproved ? '#E3F3E0' : '#F4F4F4',
							color: isApproved ? '#3C982F' : '#696969',
							padding: '4px 8px',
							borderRadius: '4px',
							display: 'inline-block',
						}}
					>
						{statusLabel}
					</span>
				);
			},
		},
		{
			title: 'Uploader Time',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 160,
			render: (text) => formatDateString(text),
		},
		{
			title: 'Update Time',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 160,
			render: (text) => formatDateString(text),
		},
		{
			title: 'Uploader',
			dataIndex: 'createdBy',
			key: 'createdBy',
			width: 150,
		},
		{
			title: 'Editor',
			dataIndex: 'updatedBy',
			key: 'updatedBy',
			width: 150,
			render: (text) => displayText(text),
		},
		{
			title: 'Approver',
			dataIndex: 'updatedBy',
			key: 'updatedBy',
			width: 150,
			render: (_, record) => {
				if (record?.status?.toLowerCase() === 'approved' || record?.status === 1 || record?.status === '1') {
					return record.updatedBy;
				} else {
					return '-';
				}
			},
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_, record) => {
				const menuItems = [
					{
						key: '1',
						icon: <DownloadOutlined style={{ fontSize: 20 }} />,
						label: 'Download',
						onClick: async () => {
							await handleDownload(record);
						},
					},
				];
				if (permissions.includes(Permissions.Image_APPROVE) && record?.status.toLowerCase() === 'pending') {
					menuItems.push({
						key: '2',
						icon: <img src={approveIcon} alt="approve" />,
						label: 'Approve',
						onClick: () => handleEditOrApprove(record, true),
					});
				}
				if (permissions.includes(Permissions.Image_EDIT) && record?.status.toLowerCase() === 'pending') {
					menuItems.push({
						key: '3',
						icon: <EditOutlined style={{ fontSize: 20 }} />,
						label: 'Edit',
						onClick: () => handleEditOrApprove(record),
					});
				}
				if (permissions.includes(Permissions.Image_DELETE)) {
					menuItems.push({
						key: '4',
						icon: <img src={deleteAction} alt="delete" />,
						label: 'Delete',
						onClick: () => handleDelete(record.id, record.ossKey, record.filename),
					});
				}

				if (menuItems.length > 1) {
					return (
						<div className="action-drop" style={{ textAlign: 'center' }}>
							<Dropdown
								defaultOpen={true}
								placement="left"
								trigger={['click']}
								style={{ marginLeft: '-80px' }}
								menu={{
									items: menuItems,
								}}
							>
								<div style={{ cursor: 'pointer' }}>
									<img alt="more" src={moreIcon} />
								</div>
							</Dropdown>
						</div>
					);
				}
			},
		},
	];

	// MARK: - render
	function _modalContent() {
		if (isBatchDelete.current) {
			return (
				<>
					<div>The selected files will be removed from the knowledge base</div>
					<div>and answer accuracy may be reduced. Are you sure to delete these images?</div>
				</>
			);
		} else {
			return (
				<>
					<div>The following image will be removed from the knowledge base</div>
					<div>{deleteTarget?.filename}</div>
				</>
			);
		}
	}

	return (
		<div className="image-base-container app-page-container">
			<div className="app-navbar">
				<div className="app-navbar-text">Image Base Management</div>
				<div className="header-right">
					<div className="application-drop">
						<Select value={selectedAppId.current} onChange={selectedAppChanged}>
							{applicationOptions.map((item) => (
								<Option key={item.key} value={item.key}>
									{item.label}
								</Option>
							))}
						</Select>
					</div>
					<div
						ref={usePermissions([Permissions.Image_IMPORT], permissions)}
						className="app-btn app-btn-primary"
						onClick={handleUpload}
					>
						<img src={addIcon} className="app-btn-icon" />
						Upload New
					</div>
				</div>
			</div>

			<AddImageModal
				visible={isModalVisible}
				mode={modalMode}
				appId={selectedAppId.current}
				editPreviewImage={editPreviewImage}
				editImageName={editImageName}
				editImageFile={editImageFile} // 添加原图片文件
				editRecordId={editRecordId}
				editOssKey={editOssKey}
				editFormValues={editFormValues}
				onCancel={handleModalCancel}
				onSave={() => handleResetFilter()}
				onUploadStatusChange={(value) => {
					setHasUploaded(value);
				}}
			/>
			{previewVisible && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 1000,
						cursor: 'pointer',
					}}
					onClick={handlePreviewClose}
				>
					<div
						style={{
							position: 'relative',
							maxWidth: '90%',
							maxHeight: '90%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<img
							src={previewImage}
							alt={previewTitle}
							style={{
								maxWidth: '100%',
								maxHeight: '100%',
								objectFit: 'contain',
								borderRadius: 8,
								boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
							}}
						/>
						<button
							onClick={handlePreviewClose}
							style={{
								position: 'absolute',
								top: -40,
								right: 0,
								background: 'none',
								border: 'none',
								color: 'white',
								fontSize: 30,
								cursor: 'pointer',
								padding: '5px 10px',
							}}
						>
							×
						</button>
						<div
							style={{
								position: 'absolute',
								bottom: -30,
								left: '50%',
								transform: 'translateX(-50%)',
								color: 'white',
								fontSize: 14,
								textAlign: 'center',
								maxWidth: '100%',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{previewTitle}
						</div>
					</div>
				</div>
			)}

			<div className="app-filter-section">
				<div className="filter-items-row">
					<Select placeholder="Issue Type" value={imageType} onChange={setImageType} className="filter-item" allowClear>
						{imageIssueType.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
					<Select placeholder="Issue Scenario" value={scene} onChange={setScene} className="filter-item" allowClear>
						{issueScenarioOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
					<Select
						placeholder="Approval Status"
						value={approvalStatus}
						onChange={setApprovalStatus}
						className="filter-item"
						allowClear
					>
						{approvalStatusOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</div>

				<div className="filter-items-row">
					<div className="filter-item">
						<RangePicker
							showTime={{ format: 'HH:mm' }}
							format="YYYY-MM-DD HH:mm"
							value={dateRange}
							onChange={(dates) => setDateRange(dates)}
							placeholder={['Update Start Time', 'Update End Time']}
						/>
					</div>
					<div className="filter-actions flex-2">
						<Button color="purple" variant="filled" onClick={handleSearch}>
							Search
						</Button>
						<Button variant="outlined" onClick={async () => await handleResetFilter()}>
							Reset
						</Button>
					</div>
				</div>
			</div>

			{selectedRowKeys.length > 0 && permissions.includes(Permissions.Image_DELETE) && (
				<div className="batch-delete-header">
					<span className="desc">Please select more items to</span>
					<div className="btn" onClick={handleBatchDelete}>
						<img src={deleteBatch} alt="batch delete" />
						<span>Batch Delete</span>
					</div>
				</div>
			)}

			<div className="app-table">
				<div className="app-table-content">
					<Table
						rowKey="id"
						size="small"
						dataSource={dataSource}
						columns={columns}
						rowSelection={rowSelection}
						pagination={false}
						scroll={{ x: 'max-content' }}
						onChange={handleTableChange}
					/>
				</div>
			</div>
			<div className="table-line-all">
				<PaginationComponent
					pagination={pagination}
					onChange={(current, pageSize) => {
						setPagination({ ...pagination, current, pageSize });
						fetchImageList(current, pageSize, false, selectedAppId.current);
					}}
					isImage={true}
				/>
			</div>

			<ConfirmModal
				visible={deleteModalVisible}
				onCancel={() => setDeleteModalVisible(false)}
				onConfirm={onConfirmDelete}
				title={isBatchDelete.current ? 'Confirm to Batch Delete' : 'Confirm to Delete'}
				contentElement={_modalContent()}
			/>
		</div>
	);
};

export default ImageBase;
